// Deposit API utility functions

const API_BASE_URL = process.env.REACT_APP_API_URL || "/api";

/**
 * Submit a new deposit request
 * @param {Object} depositData - Deposit information
 * @returns {Promise<Object>} API response
 */
export const submitDepositRequest = async (depositData) => {
  try {
    const formData = new FormData();

    // Add all deposit fields
    Object.keys(depositData).forEach(key => {
      if (depositData[key] !== null && depositData[key] !== undefined) {
        formData.append(key, depositData[key]);
      }
    });

    const response = await fetch(`${API_BASE_URL}/deposits/create`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("authToken") || ""}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to submit deposit request");
    }

    return data;
  } catch (error) {
    console.error("Deposit submission error:", error);
    throw error;
  }
};

/**
 * Get deposit history for a user
 * @param {string} userId - User ID
 * @returns {Promise<Array>} List of deposits
 */
export const getDepositHistory = async (userId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/deposits/history/${userId}`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("authToken") || ""}`,
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch deposit history");
    }

    return data.deposits || [];
  } catch (error) {
    console.error("Fetch deposit history error:", error);
    throw error;
  }
};

/**
 * Get deposit status by ID
 * @param {string} depositId - Deposit ID
 * @returns {Promise<Object>} Deposit details
 */
export const getDepositStatus = async (depositId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/deposits/status/${depositId}`, {
      method: "GET",
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("authToken") || ""}`,
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch deposit status");
    }

    return data;
  } catch (error) {
    console.error("Fetch deposit status error:", error);
    throw error;
  }
};

/**
 * Cancel a pending deposit request
 * @param {string} depositId - Deposit ID
 * @returns {Promise<Object>} API response
 */
export const cancelDepositRequest = async (depositId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/deposits/cancel/${depositId}`, {
      method: "PUT",
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("authToken") || ""}`,
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to cancel deposit request");
    }

    return data;
  } catch (error) {
    console.error("Cancel deposit error:", error);
    throw error;
  }
};

/**
 * Validate deposit amount
 * @param {number} amount - Amount to validate
 * @param {string} paymentMethod - Payment method
 * @returns {Object} Validation result
 */
export const validateDepositAmount = (amount, paymentMethod) => {
  const limits = {
    bank: { min: 100, max: 1000000 },
    upi: { min: 100, max: 100000 },
    online: { min: 100, max: 50000 }
  };

  const limit = limits[paymentMethod] || limits.bank;

  if (!amount || isNaN(amount)) {
    return { valid: false, error: "Please enter a valid amount" };
  }

  if (amount < limit.min) {
    return { valid: false, error: `Minimum deposit amount is ₹${limit.min}` };
  }

  if (amount > limit.max) {
    return { valid: false, error: `Maximum deposit amount is ₹${limit.max.toLocaleString()}` };
  }

  return { valid: true, error: null };
};

/**
 * Format deposit data for submission
 * @param {Object} rawData - Raw form data
 * @returns {Object} Formatted deposit data
 */
export const formatDepositData = (rawData) => {
  const {
    amount,
    paymentMethod,
    transactionRef,
    receipt,
    bankDetails,
    upiDetails
  } = rawData;

  const depositData = {
    amount: parseFloat(amount),
    payment_method: paymentMethod,
    transaction_ref: transactionRef,
    status: "pending",
    created_at: new Date().toISOString(),
    user_id: localStorage.getItem("userId") || "",
  };

  // Add payment method specific data
  if (paymentMethod === "bank" && bankDetails) {
    depositData.bank_name = bankDetails.bankName;
    depositData.account_number = bankDetails.accountNumber;
    depositData.ifsc_code = bankDetails.ifscCode;
  } else if (paymentMethod === "upi" && upiDetails) {
    depositData.upi_id = upiDetails.upiId;
  }

  // Add receipt if exists
  if (receipt) {
    depositData.receipt = receipt;
  }

  return depositData;
};

/**
 * Generate transaction reference number
 * @returns {string} Transaction reference
 */
export const generateTransactionRef = () => {
  const timestamp = Date.now();
  const random = Math.floor(Math.random() * 10000);
  return `TXN${timestamp}${random}`;
};

/**
 * Upload receipt image to server
 * @param {File} file - Receipt file
 * @returns {Promise<string>} Uploaded file URL
 */
export const uploadReceipt = async (file) => {
  try {
    const formData = new FormData();
    formData.append("receipt", file);

    const response = await fetch(`${API_BASE_URL}/uploads/receipt`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${localStorage.getItem("authToken") || ""}`,
      },
      body: formData,
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || "Failed to upload receipt");
    }

    return data.url;
  } catch (error) {
    console.error("Receipt upload error:", error);
    throw error;
  }
};

/**
 * Get payment method details
 * @param {string} method - Payment method type
 * @returns {Object} Payment method configuration
 */
export const getPaymentMethodConfig = (method) => {
  const configs = {
    bank: {
      name: "Bank Transfer",
      icon: "🏦",
      processingTime: "2-4 hours",
      requiresReceipt: true,
      requiresRef: true,
      instructions: "Transfer amount to company bank account and upload receipt"
    },
    upi: {
      name: "UPI Payment",
      icon: "📱",
      processingTime: "30 minutes",
      requiresReceipt: true,
      requiresRef: true,
      instructions: "Scan QR code or use UPI ID and upload payment screenshot"
    },
    online: {
      name: "Online Payment",
      icon: "💳",
      processingTime: "Instant",
      requiresReceipt: false,
      requiresRef: false,
      instructions: "Pay using cards, net banking, or wallets"
    }
  };

  return configs[method] || configs.bank;
};

export default {
  submitDepositRequest,
  getDepositHistory,
  getDepositStatus,
  cancelDepositRequest,
  validateDepositAmount,
  formatDepositData,
  generateTransactionRef,
  uploadReceipt,
  getPaymentMethodConfig
};